import React, { useState, useEffect } from 'react'

import { Button, Card, Form, Row, Typography, notification, Icon } from 'antd'
import { Formik } from 'formik'
import * as yup from 'yup'

import { CustomBreadcrumb } from '../../components/CustomBreadcrumb/CustomBreadcrumb'
import CustomInput from '../../components/CustomInput/customInput'
import CustomSwitch from '../../components/CustomSwitch/customSwitch'
import { validRule, RULES } from '../../constants/Entitlements'
import { api, request } from '../../util/handlerRequestUtil'
import { getMessage } from '../../util/IntlMessages'
import moment from 'moment'

const CreateReferencePeriod = function ({ match, history }) {
  const defaultFormValues = { active: true }
  const [formLayout] = useState('vertical')

  // Params url
  const { id } = match.params

  // Form configuration
  const [formValues, setFormValues] = useState({})

  const form = {
    initialValues: formValues,
    yupValidationCreate: yup.object().shape({
      // Create-validation-rules-body-marker
      active: yup
        .boolean()
        .typeError('must be boolean')
        .required(getMessage('isRequired', getMessage('active'))),

      name: yup
        .string()
        .max(60)
        .typeError('must be string')
        .required(getMessage('isRequired', getMessage('name'))),

      beginDate: yup.date().required(getMessage('mandatoryField')),
      endDate: yup
        .date()
        .when(
          'beginDate',
          (beginDate, schema) =>
            beginDate && schema.min(beginDate, 'Data de término deve ser posterior a Data de início')
        )
        .typeError('must be string')
        .required(getMessage('mandatoryField'))
    }),

    yupValidationUpdate: yup.object().shape({
      // Update-validation-rules-body-marker
      active: yup.boolean().typeError('must be boolean'),

      name: yup.string().max(60).typeError('must be string'),

      beginDate: yup.date().required(getMessage('mandatoryField')),
      endDate: yup
        .date()
        .when(
          'beginDate',
          (beginDate, schema) =>
            beginDate && schema.min(beginDate, 'Data de término deve ser posterior a Data de início')
        )
        .typeError('must be string')
        .required(getMessage('mandatoryField'))
    }),

    onSubmit: async (values) => {
      console.tron.log('submit values', values)
      if (!id) {
        const data = {
          name: values.name,
          beginDate: values.beginDate,
          endDate: values.endDate,
          active: values.active
        }
        storeReferencePeriod(data)
      } else {
        const payload = {
          data: {
            name: values.name,
            beginDate: values.beginDate,
            endDate: values.endDate,
            active: values.active
          },
          id
        }
        for (const key in payload.data) {
          if (payload.data[key] === null) {
            delete payload.data[key]
          }
        }

        updateReferencePeriod(payload)
      }
    }
  }

  // Mount/Umount component
  useEffect(() => {
    const hasAccess = validRule(RULES.REFERENCE_PERIOD) // Não deixa acessar a tela
    if (!hasAccess) {
      return history.push('/home')
    }

    if (id) {
      retrieveReferencePeriod(id)
    } else {
      setFormValues({})
    }

    // Use-effect-mount-marker
    return () => {
      setFormValues({})
    }
  }, [])

  useEffect(() => {
    if (!id) {
      setFormValues(defaultFormValues)
    }
  }, [id])

  // Api requests
  async function storeReferencePeriod(data) {
    const formattedData = {
      active: data.active,
      name: data.name,
      beginDate: data.beginDate,
      endDate: data.endDate
    }
    try {
      await request(api.createSafra(formattedData))
      notification.success({
        description: getMessage('successstoreReferencePeriod'),
        message: getMessage('sweetAlerts.success')
      })
      setFormValues({})
      history.goBack()
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function retrieveReferencePeriod(id) {
    try {
      const response = await request(api.getSafraById(id))
      const formattedValues = {
        id: response.id,
        active: response.active,
        name: response.name,
        beginDate: moment(String(response.beginDate).slice(0, 10)),
        endDate: moment(String(response.endDate).slice(0, 10))
      }
      console.tron.log('response', response)
      setFormValues({
        ...formattedValues
      })
    } catch (error) {
      console.tron.log(error)
    }
  }

  async function updateReferencePeriod(payload) {
    try {
      console.tron.log('updateReferencePeriod')
      const { id, data } = payload
      const formattedData = {
        active: data.active,
        name: data.name,
        beginDate: data.beginDate,
        endDate: data.endDate
      }
      await request(api.updateSafra(id, formattedData))
      notification.success({
        description: getMessage('successUpdateReferencePeriod'),
        message: getMessage('sweetAlerts.success')
      })
      history.goBack()
    } catch (error) {
      console.tron.log(error)
    }
  }

  function renderHeader(submitForm) {
    const hasAccess = id === null ? validRule(RULES.REFERENCE_PERIOD) : validRule(RULES.REFERENCE_PERIOD)

    return (
      <div className="default-header-div">
        <div className="default-header-breadcrumb">
          <CustomBreadcrumb match={match} history={history} />
          <div className="default-header-buttons">
            <Button className="button-return" onClick={() => history.goBack()}>
              <Icon type="left" theme="outlined" className="default-back-icon" />
              {getMessage('back')}
            </Button>
            {hasAccess && (
              <Button type="primary" className="button-action" onClick={() => submitForm()}>
                {id ? getMessage('update') : getMessage('register')}
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <Formik
        enableReinitialize
        validationSchema={!id ? form.yupValidationCreate : form.yupValidationUpdate}
        initialValues={formValues}
        onSubmit={(values, { setSubmitting, setFieldError, resetForm }) =>
          form.onSubmit(values, setSubmitting, setFieldError, resetForm)
        }
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ values, errors, handleBlur, handleSubmit, setFieldValue, submitForm }) => {
          const setFieldValueWithState = (field, value) => {
            setFormValues({ ...formValues, [field]: value })
            return setFieldValue(field, value)
          }

          return (
            <Form className="referencePeriod-form" layout={formLayout} onSubmit={handleSubmit}>
              {renderHeader(submitForm)}

              <Card className="ant-card gx-card ant-card-bordered">
                <Typography.Title className="default-header-title" level={3}>
                  {id ? getMessage('updateReferencePeriod') : getMessage('createReferencePeriod')}
                </Typography.Title>
                <Row type="flex" justify="start">
                  <CustomSwitch
                    field="active"
                    errors={errors}
                    setFieldValue={setFieldValueWithState}
                    rowSpan={12}
                    value={values}
                    handleBlur={handleBlur}
                    unCheckedChildren={getMessage('label.inactive')}
                    checkedChildren={getMessage('label.active')}
                  />
                </Row>
                <Row type="flex" justify="start">
                  <CustomInput
                    label={getMessage('periodName')}
                    field="name"
                    errors={errors}
                    setFieldValue={setFieldValueWithState}
                    rowSpan={6}
                    value={values}
                    handleBlur={handleBlur}
                  />
                  <CustomInput
                    type="date"
                    field="beginDate"
                    errors={errors}
                    setFieldValue={setFieldValueWithState}
                    rowSpan={6}
                    value={values}
                    format="DD/MM/YYYY"
                    handleBlur={handleBlur}
                    required
                  />
                  <CustomInput
                    type="date"
                    field="endDate"
                    errors={errors}
                    setFieldValue={setFieldValueWithState}
                    rowSpan={6}
                    value={values}
                    format="DD/MM/YYYY"
                    handleBlur={handleBlur}
                    required
                  />
                </Row>
              </Card>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default CreateReferencePeriod
